import React from 'react'
import { Form, Spin } from 'antd'
import { useLingui } from '@lingui/react/macro'
import { useMemoizedFn } from 'ahooks'

import { COVER_FORM_DEFAULTS } from '../../_store'
import { useCoverStore } from '../../_store/useCoverStore'

import { useCoverSubmit } from './_hooks/useCoverSubmit'
import CoverReferenceAudioField from './_components/CoverReferenceAudioField'
import InstrumentalSwitchField from './_components/InstrumentalSwitchField'

import type { Promisable } from 'type-fest'
import type { CoverFormValues } from '../../_types/form'

import HeaderTabs from '@/pages/tad.ai/_components/HeaderTabs'
import ScrollArea from '@/pages/tad.ai/_components/ScrollArea'
import { useScreen } from '@/hooks/screen'
import CreateButton from '@/pages/tad.ai/_components/CreateButton'
import AdvancedSettings from '@/pages/tad.ai/_components/AdvancedSettings'
import StickyFormActionLayout from '@/pages/tad.ai/_components/StickyFormActionLayout'
import StyleSelector from '@/pages/tad.ai/_components/StyleSelector'
import EnhanceTextArea from '@/components/EnhanceTextArea'
import {
  COVER_MAX_DESCRIPTION_LENGTH,
  COVER_MAX_LYRICS_LENGTH,
  COVER_MAX_STYLE_LENGTH,
} from '@/pages/tad.ai/ai-cover-generator/_constant'


import { useEffect } from 'react';
import { cls } from 'tagged-classnames-free';
export interface CoverFormProps {
  className?: string
  onCreated?: () => Promisable<void>
}

/**
 * zustand store 和 antd Form 同步 hook
 *
 * zustand store 是持久化层（单一数据源），antd Form 用于校验和布局。
 * - 挂载时：等 persist rehydrate 完成后，从 store 恢复表单值到 antd Form
 * - 编辑时：antd Form onValuesChange -> 写入 store
 */
function useFormStoreSync(
  form: ReturnType<typeof Form.useForm<CoverFormValues>>[0],
) {
  const storeForm = useCoverStore((s) => s.form)
  const hasHydrated = useCoverStore((s) => s._hasHydrated)
  const setFormValues = useCoverStore((s) => s.setFormValues)

  // persist rehydrate 完成后，将 store 值同步到 antd Form
  useEffect(() => {
    if (hasHydrated) {
      form.setFieldsValue(storeForm)
    }
    // 只在 hydrate 完成时执行一次，不需要监听 storeForm 后续变化
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated])

  /** antd Form onValuesChange -> 同步到 store */
  const syncToStore = useMemoizedFn(() => {
    setFormValues(form.getFieldsValue(true))
  })

  return { syncToStore }
}

const CoverForm: React.FC<CoverFormProps> = ({ className, onCreated }) => {
  const { t } = useLingui()
  const [form] = Form.useForm<CoverFormValues>()
  const mode = Form.useWatch('mode', form)
  const isCustom = mode === 'custom'
  const isXl = useScreen('xl')

  // ─── Store 状态订阅 ───
  const isSubmitting = useCoverStore((s) => s.ui.isSubmitting)
  const isUploading = useCoverStore((s) => s.ui.isUploading)
  const isDetecting = useCoverStore((s) => s.ui.isDetecting)
  const advancedExpanded = useCoverStore((s) => s.prefs.advancedExpanded)
  const setUI = useCoverStore((s) => s.setUI)
  const setPrefs = useCoverStore((s) => s.setPrefs)
  const setFormField = useCoverStore((s) => s.setFormField)

  const { handleSubmit } = useCoverSubmit({ onSuccess: onCreated })
  const { syncToStore } = useFormStoreSync(form)

  const stopDetecting = useMemoizedFn(() => {
    setUI({ isDetecting: false })
  })

  useEffect(() => {
    if (!isCustom) {
      stopDetecting()
    }
  }, [isCustom, stopDetecting])

  const handleDetectedLyrics = useMemoizedFn((lyrics: string) => {
    if (lyrics) {
      form.setFieldValue('customPrompt', lyrics)
      setFormField('customPrompt', lyrics)
    }
    stopDetecting()
  })

  return (
    <div
      className={cls`bg-f-other-1 scrollbar z-0 mb-6 flex-col overflow-x-hidden rounded-2xl xl:w-[680px] ${className}`}
    >
      <Form<CoverFormValues>
        layout='vertical'
        requiredMark={false}
        form={form}
        initialValues={COVER_FORM_DEFAULTS}
        className={cls`
          scrollbar-none [&_[class*="form-item-label"]>label]:text-f-text mx-4
          flex h-full flex-col
          rounded-2xl
          md:mx-8
          xl:max-w-none
          xl:overflow-auto
          [&_[class*="form-item-label"]>label]:text-lg
          [&_[class*="form-item-label"]>label]:font-semibold
        `}
        onValuesChange={() => syncToStore()}
        onFinish={async (values) => {
          try {
            await handleSubmit(values)
          } catch {
            // tRPC 全局异常处理
          }
        }}
        scrollToFirstError
      >
        {/* Tabs */}
        <Form.Item noStyle name='mode'>
          <HeaderTabs
            activeKey={mode || 'smart'}
            items={[
              { label: t`Smart`, key: 'smart' },
              { label: t`Custom`, key: 'custom' },
            ]}
            onChange={(key) => {
              form.setFieldValue('mode', key)
              syncToStore()
            }}
          />
        </Form.Item>

        {/* Form area */}
        <ScrollArea
          disabled={!isXl}
          className={cls`
            bg-f-bg-elevated flex-1 overflow-hidden rounded-2xl
            ${mode === 'smart' || !mode ? 'rounded-tl-none' : ''}
          `}
        >
          <StickyFormActionLayout
            className={cls`pt-5`}
            contentClassName={cls`px-4 md:px-6`}
            actionClassName={cls`px-4 md:px-6`}
            action={
              <CreateButton
                loading={isSubmitting}
                disabled={isUploading || (isCustom && isDetecting)}
              />
            }
          >
            <CoverReferenceAudioField
              form={form}
              onCancelDetecting={stopDetecting}
              onDetectedLyrics={isCustom ? handleDetectedLyrics : undefined}
              onDraftChange={syncToStore}
            />

            <Spin
              spinning={isCustom && isDetecting}
              tip={t`Detecting lyrics...`}
            >
              <Form.Item
                name={isCustom ? 'customPrompt' : 'smartPrompt'}
                label={isCustom ? t`Lyrics` : t`Description`}
                rules={
                  isCustom
                    ? [{ message: t`Please enter lyrics` }]
                    : [
                        {
                          required: true,
                          message: t`Please enter a description`,
                        },
                      ]
                }
              >
                <EnhanceTextArea
                  placeholder={
                    isCustom
                      ? t`Enter lyrics here, or we will auto-detect them from the uploaded audio.`
                      : t`Describe the music you want. Include the genre, vibe, and a brief description of your content.`
                  }
                  size='large'
                  count={{
                    show: true,
                    max: isCustom
                      ? COVER_MAX_LYRICS_LENGTH
                      : COVER_MAX_DESCRIPTION_LENGTH,
                  }}
                  rows={6}
                  showClear
                  disabled={isCustom && isDetecting}
                  style={{ resize: 'none' }}
                  className={cls`text-sm`}
                  containerClassName={cls`
                    border-f-border-secondary
                    bg-f-bg-elevated
                    hover:bg-f-bg-elevated
                  `}
                />
              </Form.Item>
            </Spin>

            <div className={cls`${isCustom ? 'hidden' : 'block'}`}>
              <InstrumentalSwitchField />
            </div>

            <div className={cls`${isCustom ? 'block' : 'hidden'}`}>
              <Form.Item
                name='style'
                label={t`Style`}
                rules={
                  isCustom
                    ? [
                        {
                          required: true,
                          message: t`Please select or enter a style`,
                        },
                      ]
                    : undefined
                }
              >
                <StyleSelector maxLength={COVER_MAX_STYLE_LENGTH} />
              </Form.Item>

              <Form.Item name='advanced'>
                <AdvancedSettings
                  expanded={advancedExpanded}
                  onExpandedChange={(expanded) =>
                    setPrefs({ advancedExpanded: expanded })
                  }
                />
              </Form.Item>
            </div>
          </StickyFormActionLayout>
        </ScrollArea>
      </Form>
    </div>
  )
}

export default CoverForm
